import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { brandConfig } from '../config';
import { safeGetItem, safeSetItem } from '../utils/safeStorage';

// Theme mode type
type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  defaultMode = 'light',
  storageKey = 'owner-portal-theme'
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window !== 'undefined') {
      const stored = safeGetItem('local', storageKey);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    }
    return defaultMode;
  });

  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>('light');

  // Resolve the actual mode based on system preference
  useEffect(() => {
    const resolveMode = () => {
      if (mode === 'system') {
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setResolvedMode(systemPrefersDark ? 'dark' : 'light');
      } else {
        setResolvedMode(mode);
      }
    };

    resolveMode();

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (mode === 'system') {
        resolveMode();
      }
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [mode]);

  // Apply theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedMode);
  }, [resolvedMode]);

  const setMode = useCallback(
    (newMode: ThemeMode) => {
      setModeState(newMode);
      safeSetItem('local', storageKey, newMode);
    },
    [storageKey]
  );

  const toggleMode = useCallback(() => {
    setMode(resolvedMode === 'light' ? 'dark' : 'light');
  }, [resolvedMode, setMode]);

  const contextValue = useMemo(
    () => ({ mode, resolvedMode, setMode, toggleMode }),
    [mode, resolvedMode, setMode, toggleMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <Helmet>
        <meta name="theme-color" content={resolvedMode === 'dark' ? '#0a0e14' : '#ffffff'} />
        <link rel="manifest" href={`/${brandConfig.brandCode.toLowerCase()}/manifest.json`} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`/${brandConfig.brandCode.toLowerCase()}/apple-touch-icon.png`}
        />
        <link rel="icon" href={`/${brandConfig.brandCode.toLowerCase()}/favicon.ico`} />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`/${brandConfig.brandCode.toLowerCase()}/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`/${brandConfig.brandCode.toLowerCase()}/favicon-16x16.png`}
        />
      </Helmet>
      {children}
    </ThemeContext.Provider>
  );
}
